import { HttpContext } from '@angular/common/http';
import { Injectable, Signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { NgxI18nService } from '@studiohyperdrive/ngx-i18n';
import { dispatchDataToStore, StoreService } from '@studiohyperdrive/ngx-store';
import { validateContent, ObservableArray, ObservableBoolean } from '@studiohyperdrive/rxjs-utils';
import { combineLatest, filter, first, map, Observable, of, shareReplay, switchMap, take, tap } from 'rxjs';

import { SERVER_ERROR_HANDLER } from '@cjm/shared/authentication/auth';
import { convertProductResultToProduct, FavoriteProductsService } from '@cjm/shared/products';
import { ProductEntityResult, UserTargetCodeEntity } from '@cjm/shared/types';
import { catchWithSnackBar, SnackBarService } from '@cjm/shared/ui/toast';
import { UserEntity, UserService } from '@cjm/shared/user';
import {
	MyAssociationAPIService,
	myAssociationSelectors,
	myAssociationActions,
	advicePagesSelectors,
	advicePagesActions,
	AdvicePagesApiService,
	advicePageMapper
} from '@cjm/v-loket/repositories';
import {
	AdviceDetailPageEntity,
	AdvicePagesFilter,
	IconListItem,
	MyAssociationEntity,
	parseProgressTasksToIconListItems,
	I18nKeys as VLOI18nKeys,
	parseAssociation
} from '@cjm/v-loket/shared';

import { I18nKeys } from '../../../i18n';
import { InTheSpotlightApiService, InTheSpotlightResultEntity } from '../../services';
import { actions, InTheSpotlightEntity, selectors } from '../../store';

@Injectable()
export class HomeFacade extends StoreService {
	private readonly sharedI18nKeys: typeof I18nKeys = I18nKeys;

	public readonly myAssociationLoading$: ObservableBoolean = this.selectLoadingFromStore(
		myAssociationSelectors.myAssociationData
	).pipe(shareReplay());
	public readonly myAssociationError$: ObservableBoolean = this.selectErrorFromStore(
		myAssociationSelectors.myAssociationData
	);
	public readonly myAssociation$: Observable<MyAssociationEntity> = this.userService.loading$.pipe(
		// Denis: Only proceed when the user-session request has been completed
		filter((loading) => !loading),
		// Denis: Switch to the user-session
		switchMap(() => this.userService.user$),
		switchMap((user: UserEntity) => {
			// Denis: If the user is not an association, stop here.
			if (typeof user?.company?.vCode !== 'string' || user.company.vCode.length === 0) {
				return of(null);
			}

			// Denis: Else, return the my-association loading object from the store.
			return this.myAssociationLoading$;
		}),
		// Denis: Validate the content and only emit when the loading is false (or null = not a VER)
		validateContent({
			customValidatorFn: (loading: boolean) => loading === null || !loading
		}),
		switchMap((loading: boolean = null) => {
			// Denis: If the previous value is `null`, the user is not a VER and we need to stop here.
			if (loading === null) {
				return of(null);
			}

			// Denis: SwitchMap to the my-association object from the store.
			return this.selectFromStore<MyAssociationEntity>(myAssociationSelectors.myAssociationData);
		}),
		// Denis: Share this base stream instead of recreating it.
		shareReplay()
	);
	public readonly myAssociationProgressCompleted$: ObservableBoolean = this.myAssociation$.pipe(
		map((data: MyAssociationEntity) => {
			// Denis: If percentage is non-existent, return false.
			if (isNaN(data?.metaData?.progress?.percentage)) {
				return false;
			}

			// Denis: The percentage is offered as a floating point number by the API.
			return Number((data.metaData.progress.percentage * 100).toFixed(0)) === 100;
		})
	);
	public readonly myAssociationProgress$: ObservableArray<IconListItem> = this.myAssociation$.pipe(
		validateContent({ strict: true }),
		catchWithSnackBar(this.snackBarService, {
			title: this.i18nService.getTranslation(this.sharedI18nKeys.SnackBars.ErrorTitle)
		}),
		map((data: MyAssociationEntity): IconListItem[] => {
			const iconLabels = {
				done: this.i18nService.getTranslation(VLOI18nKeys.SharedAssociations.IconList.Alt.Done),
				todo: this.i18nService.getTranslation(VLOI18nKeys.SharedAssociations.IconList.Alt.Todo)
			};

			// Denis: If the percentage is higher than 50%, only parse the incomplete tasks to abbreviate the list.
			if (data?.metaData?.progress.percentage > 0.5) {
				return parseProgressTasksToIconListItems(
					data?.metaData?.progress?.tasks.filter((task) => !task.isCompleted),
					this.i18nService.currentLanguage,
					iconLabels
				);
			}

			// Denis: Parse the full list.
			return parseProgressTasksToIconListItems(
				data?.metaData?.progress?.tasks,
				this.i18nService.currentLanguage,
				iconLabels
			);
		})
	);

	// Spotlight: Advice Pages
	public readonly advicePages$: ObservableArray<AdviceDetailPageEntity> = this.selectFromStore<
		AdviceDetailPageEntity[]
	>(advicePagesSelectors.advicePages);
	public readonly advicePagesLoading$: ObservableBoolean = this.selectLoadingFromStore(
		advicePagesSelectors.advicePages
	);

	// Spotlight: Associations & Products
	public readonly spotlightItems$: Observable<InTheSpotlightEntity> = this.selectFromStore(selectors.inTheSpotlight);
	public readonly spotlightItemsLoading$: ObservableBoolean = this.selectLoadingFromStore(selectors.inTheSpotlight);
	public readonly settingFavoriteInProgress: Signal<number | string> = this.favoriteService.actionInProgress;

	constructor(
		protected readonly userService: UserService,
		protected readonly i18nService: NgxI18nService,
		protected readonly myAssociationAPIService: MyAssociationAPIService,
		protected readonly snackBarService: SnackBarService,
		private readonly advicePagesApiService: AdvicePagesApiService,
		private readonly inTheSpotlightApiService: InTheSpotlightApiService,
		private readonly favoriteService: FavoriteProductsService,
		protected readonly store: Store
	) {
		super(store);
	}

	/**
	 * init
	 *
	 * The init method can be called from the page to fetch initial data.
	 */
	public init(): ObservableBoolean {
		this.getAdvicePages().pipe().subscribe();
		this.getSpotlightItems().pipe().subscribe();
		return combineLatest([this.userService.isCompany$, this.userService.userHasVCode()]).pipe(
			filter(([isCompany, hasVCode]: [boolean, boolean]) => isCompany && hasVCode),
			switchMap(() => this.getMyAssociation()),
			map(() => true)
		);
	}

	/**
	 * getMyAssociation
	 *
	 * The getMyAssociation method will fetch the association and dispatch it to the store.
	 * It will not return a value since this is a dispatch action to the store.
	 */
	private getMyAssociation(): ObservableBoolean {
		return combineLatest([
			this.myAssociation$,
			this.selectLoadingFromStore(myAssociationSelectors.myAssociationData)
		]).pipe(
			first(),
			switchMap(([data, loading]: [MyAssociationEntity, boolean]) => {
				// Denis: If the data is not yet provided in the store, fetch and store it.
				if (!data && !loading) {
					return dispatchDataToStore<MyAssociationEntity>(
						myAssociationActions.myAssociationData,
						this.myAssociationAPIService.getMyAssociation(
							null,
							new HttpContext().set(SERVER_ERROR_HANDLER, true)
						),
						this.store
					);
				}

				// Denis: If the data is provided in the store, return it.
				return of(data);
			}),
			catchWithSnackBar(this.snackBarService, {
				title: this.i18nService.getTranslation(this.sharedI18nKeys.SnackBars.ErrorTitle)
			})
		);
	}

	/**
	 * getAdvicePages
	 *
	 * The getAdvicePages method will fetch the requested page through the API and store the data.
	 *
	 * @param dispatchType{ 'set' | 'add' } - The dispatch type to the store, defaults to 'set'
	 *
	 * @returns ObservableBoolean
	 */
	public getAdvicePages(dispatchType: 'set' | 'add' = 'set'): ObservableBoolean {
		return dispatchDataToStore<AdviceDetailPageEntity[]>(
			advicePagesActions.advicePages,
			this.advicePagesApiService
				.getAdvicePages({
					recommended: true,
					index: 1,
					size: 3
				} as AdvicePagesFilter)
				.pipe(
					// Validate if the response has content
					validateContent({ strict: true }),
					// Map the data to fit the required format
					map((data) => advicePageMapper(data.pages))
				),
			this.store,
			dispatchType
		).pipe(
			catchWithSnackBar(this.snackBarService, {
				title: this.i18nService.getTranslation(this.sharedI18nKeys.SnackBars.ErrorTitle)
			}),
			take(1),
			// Map to a boolean to avoid subscribing to the data
			map(() => true)
		);
	}

	/**
	 * Fetch the offered associations from the api and dispatch them to the store
	 *
	 * @returns ObservableBoolean
	 */
	public getSpotlightItems(): ObservableBoolean {
		return this.userService.loading$.pipe(
			filter((loading) => !loading),
			take(1),
			switchMap(() =>
				dispatchDataToStore(
					actions.inTheSpotlight,
					this.inTheSpotlightApiService.getSpotlightItems().pipe(map(this.convertSpotLightItems)),
					this.store
				)
			),
			catchWithSnackBar(this.snackBarService, {
				title: this.i18nService.getTranslation(this.sharedI18nKeys.SnackBars.ErrorTitle)
			}),
			take(1),
			// Map to a boolean to avoid subscribing to the data
			map(() => true)
		);
	}

	/**
	 * setProductAsFavorite
	 *
	 * The setProductAsFavorite method will mark a product as favorite for current user's association.
	 *
	 * @param productId - The ID of the product to be marked as favorite
	 *
	 */
	public setProductAsFavorite(productId: string | number): Observable<unknown> {
		return this.favoriteService.setProductAsFavorite(productId).pipe(
			switchMap(() => {
				return this.selectFromStore<InTheSpotlightEntity>(selectors.inTheSpotlight);
			}),
			first(),
			tap((items: InTheSpotlightEntity) => {
				if (!items) {
					return;
				}

				this.store.dispatch(
					actions.inTheSpotlight.set({
						payload: {
							...items,
							products: items.products.map((product) =>
								product.id === productId ? { ...product, isFavorite: true } : product
							)
						}
					})
				);
			})
		);
	}

	/**
	 * removeProductFromFavorites
	 *
	 * The removeProductFromFavorites method will unmark a product as favorite for current user's association.
	 *
	 * @param productId - The ID of the product to be unmarked as favorite
	 */
	public removeProductFromFavorites(productId: string | number): Observable<unknown> {
		return this.favoriteService.removeProductFromFavorites(productId).pipe(
			switchMap(() => {
				return this.selectFromStore<InTheSpotlightEntity>(selectors.inTheSpotlight);
			}),
			first(),
			tap((items: InTheSpotlightEntity) => {
				if (!items) {
					return;
				}

				this.store.dispatch(
					actions.inTheSpotlight.set({
						payload: {
							...items,
							products: items.products.map((product) =>
								product.id === productId ? { ...product, isFavorite: false } : product
							)
						}
					})
				);
			})
		);
	}

	/**
	 * showFavoriteLoginModal
	 *
	 * The showFavoriteLoginModal method will show the login modal for the favorite products through the favoriteService.
	 *
	 * @param targetCode{UserTargetCodeEntity} - The target code for the favorite login modal
	 * @param callbackUrl{string} - The callback URL for the favorite login modal, defaults to '/'
	 */
	public showFavoriteLoginModal(targetCode: UserTargetCodeEntity, callbackUrl: string = '/'): void {
		this.favoriteService.showLoginModal(targetCode, callbackUrl);
	}

	/**
	 * convertSpotLightItems converts elements in api call result in to InTheSpotlightEntity
	 *
	 * Elements in api results can contain AssociationResultEntity or ProductEntityResult in the same array
	 * this method splits them in two separate arrays
	 *
	 * @param {InTheSpotlightResultEntity} spotlightItems
	 *
	 * @returns InTheSpotlightEntity
	 */
	private convertSpotLightItems = (spotlightItems: InTheSpotlightResultEntity): InTheSpotlightEntity => {
		const items =
			Array.isArray(spotlightItems?.elementen) && spotlightItems.elementen.length > 0
				? spotlightItems.elementen
				: [];

		return items.reduce(
			(acc, item) => {
				if (item.id === 'verenigingen' && item.succes === true) {
					return {
						...acc,
						associations:
							Array.isArray(item.elementen) && item.elementen.length > 0
								? item.elementen.map((element) => parseAssociation(element))
								: []
					};
				}

				if (item.id === 'producten' && item.succes === true) {
					return {
						...acc,
						products:
							Array.isArray(item.elementen) && item.elementen.length > 0
								? item.elementen.map((element) =>
										convertProductResultToProduct(element.product as ProductEntityResult)
									)
								: []
					};
				}

				return acc;
			},
			{
				associations: [],
				products: []
			}
		);
	};
}
